// ─── wordOoxml.js ─────────────────────────────────────────────────────────────
// Shared WordprocessingML helpers used by BOTH the legacy WordExport and the v3
// Condition Report engine. Pure string/OOXML manipulation + the proven photo-embed
// pipeline (fetch bytes → media → rels → content-type → aspect-fit → token swap).
export const RUN_RPR = '<w:rFonts w:ascii="Century Gothic" w:hAnsi="Century Gothic"/><w:sz w:val="18"/><w:szCs w:val="18"/>';

// Escapes `"` as well as `& < >`: this helper is shared with the v3 engine, which may
// place values inside XML attributes where a bare quote would break the document.
export function esc(s) {
  return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ── Inject a text run before the first </w:p> in a cell ──
export function injectText(cell, text, opts = {}) {
  let rpr = RUN_RPR;
  if (opts.bold) rpr += '<w:b/>';
  if (opts.color) rpr += '<w:color w:val="' + opts.color + '"/>';
  const run = '<w:r><w:rPr>' + rpr + '</w:rPr><w:t xml:space="preserve">' + esc(text) + '</w:t></w:r>';
  return cell.replace(/(<\/w:pPr>)(\s*)(<\/w:p>)/, '$1' + run + '$3');
}

// The exact run that carries a @@PHOTO_n@@ token. Producers emit it; embedPhotoQueue
// swaps it for a drawing by exact string match — so both sides MUST build it here.
export function photoTokenRun(token) {
  return '<w:r><w:rPr>' + RUN_RPR + '</w:rPr><w:t xml:space="preserve">' + token + '</w:t></w:r>';
}

// ── Build a sized inline drawing referencing an embedded image ──
export function buildDrawing(rid, wEmu, hEmu, docprId) {
  return '<w:r><w:drawing><wp:inline distT="0" distB="0" distL="0" distR="0">'
    + '<wp:extent cx="' + wEmu + '" cy="' + hEmu + '"/>'
    + '<wp:effectExtent l="0" t="0" r="0" b="0"/>'
    + '<wp:docPr id="' + docprId + '" name="Photo' + docprId + '"/>'
    + '<wp:cNvGraphicFramePr><a:graphicFrameLocks xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" noChangeAspect="1"/></wp:cNvGraphicFramePr>'
    + '<a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">'
    + '<a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">'
    + '<pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">'
    + '<pic:nvPicPr><pic:cNvPr id="' + docprId + '" name="Photo' + docprId + '"/><pic:cNvPicPr/></pic:nvPicPr>'
    + '<pic:blipFill><a:blip r:embed="' + rid + '"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill>'
    + '<pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="' + wEmu + '" cy="' + hEmu + '"/></a:xfrm>'
    + '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr>'
    + '</pic:pic></a:graphicData></a:graphic></wp:inline></w:drawing></w:r>';
}

// ── Table helpers: split into rows/cells (regex matches the simple uniform tables) ──
export function tableRows(tableXml) {
  return tableXml.match(/<w:tr\b[\s\S]*?<\/w:tr>/g) || [];
}
export function rowCells(rowXml) {
  return rowXml.match(/<w:tc>[\s\S]*?<\/w:tc>/g) || [];
}
export function cellText(cellXml) {
  const ts = cellXml.match(/<w:t[ >][\s\S]*?<\/w:t>/g) || [];
  return ts.map(t => t.replace(/<[^>]+>/g, '')).join('').trim();
}

// Replace each <w:tc>…</w:tc> in a row, in order, with the supplied cell XML.
// Positional (not string-match) so it's safe even when two cells are identical.
export function setRowCells(rowXml, newCells) {
  let i = 0;
  return rowXml.replace(/<w:tc>[\s\S]*?<\/w:tc>/g, () => (i < newCells.length ? newCells[i++] : ''));
}

// Derive a pristine, empty row scaffold from a real body row: strip every run
// (so each cell is an empty paragraph injectText can fill) and drop the paraId/
// textId so cloned rows don't duplicate the template's ids.
export function blankRowFrom(rowXml) {
  return rowXml
    .replace(/<w:r\b[\s\S]*?<\/w:r>/g, '')
    .replace(/\s+w14:paraId="[^"]*"/g, '')
    .replace(/\s+w14:textId="[^"]*"/g, '');
}

// Normalise a label for matching: decode the entities cellText leaves raw
// (&amp; in "Gutters & Downpipes"), unify dash variants (en/em-dash → hyphen),
// collapse whitespace, lower-case. Makes report-row matching robust to the
// template's exact punctuation.
export function normLabel(s) {
  return (s || '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/[‐-―]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// Embed every queued photo: fetch bytes, add to media, register rels + jpeg content
// type, size to fit its box preserving aspect ratio, and swap the exact token run for
// a drawing. Returns the updated document xml. (Lifted from WordExport step 5.)
export async function embedPhotoQueue(zip, xml, queue) {
  if (!queue.length) return xml;
  let relsXml = await zip.file('word/_rels/document.xml.rels').async('text');
  let ctXml = await zip.file('[Content_Types].xml').async('text');
  if (!/Extension="jpeg"/.test(ctXml)) {
    ctXml = ctXml.replace('</Types>', '<Default Extension="jpeg" ContentType="image/jpeg"/></Types>');
  }
  const usedIds = (relsXml.match(/Id="rId(\d+)"/g) || []).map(s => parseInt(s.replace(/\D/g, ''), 10));
  let nextRid = (usedIds.length ? Math.max(...usedIds) : 0) + 1;

  for (let i = 0; i < queue.length; i++) {
    const item = queue[i];
    try {
      const pResp = await fetch(item.url);
      if (!pResp.ok) throw new Error('photo fetch failed');
      const bytes = await pResp.arrayBuffer();
      const fname = 'cr_photo' + i + '.jpeg';
      zip.file('word/media/' + fname, bytes);
      const rid = 'rId' + nextRid; nextRid += 1;
      relsXml = relsXml.replace('</Relationships>',
        '<Relationship Id="' + rid + '" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/' + fname + '"/></Relationships>');
      // Fit the image inside its target box while PRESERVING aspect ratio (no
      // squashing). Decode the natural pixel size and scale down to fit the box;
      // fall back to the box dimensions if decoding isn't available.
      const maxW = item.maxW || 2200000, maxH = item.maxH || 1650000; // EMU box (~2.4×1.8in)
      let W = maxW, H = maxH;
      try {
        const bmp = await createImageBitmap(new Blob([bytes]));
        const iwEmu = bmp.width * 9525, ihEmu = bmp.height * 9525; // 9525 EMU per px @96dpi
        if (typeof bmp.close === 'function') bmp.close();
        if (iwEmu > 0 && ihEmu > 0) {
          const scale = Math.min(maxW / iwEmu, maxH / ihEmu);
          W = Math.max(1, Math.round(iwEmu * scale));
          H = Math.max(1, Math.round(ihEmu * scale));
        }
      } catch { /* keep box dimensions */ }
      const drawing = buildDrawing(rid, W, H, item.docprId);
      // Replace the EXACT token run (built by injectText with RUN_RPR) with the
      // drawing. Must be an exact string swap: a lazy `<w:r><w:rPr>[\s\S]*?token`
      // regex matches from the first run in the document and spans across every
      // intervening paragraph/table to reach the token, corrupting the file.
      const tokenRun = photoTokenRun(item.token);
      xml = xml.split(tokenRun).join(drawing);
    } catch (e) {
      // On failure, just remove the token so no @@PHOTO@@ text leaks into the doc
      xml = xml.split(item.token).join('');
    }
  }
  zip.file('word/_rels/document.xml.rels', relsXml);
  zip.file('[Content_Types].xml', ctXml);
  return xml;
}
